import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { InformationMessage, ThemedAsset, assetIds } from "nemuru-components";
import React, { useContext, useState } from "react";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import IconTitle from "../../../../shared/components/IconTitle";
import MainButton from "../../../../shared/components/MainButton";
import ResponsiveDialog from "../../../../shared/components/ResponsiveDialog";
import ResponsiveLayout from "../../../../shared/components/ResponsiveLayout";
import Text from "../../../../shared/components/Text";
import { FILE_TYPE, documentStatus } from "../config";
import DocumentationFile from "./DocumentationFile";
import { DocumentStatus } from "@nemuru-eng/js-api-client";

export default function DocumentRequiredDialog({
  files,
  fadeOut,
  open,
  onClose,
  onSubmit,
  addFile,
  removeFile,
  documentRequired,
  filesUploading,
}) {
  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);

  if (documentRequired === undefined) {
    return null;
  }

  const { palette } = useTheme();
  const [uploadingState, setUploadingState] = useState(false);
  const [confirmationState, setConfirmationState] = useState(false);

  const onDone = () => {
    setConfirmationState(false);
    setUploadingState(true);
    onSubmit();
  };
  const onClickClose = () => {
    if (files.length) {
      setConfirmationState(true);
    } else {
      onClose();
    }
  };

  const documentRequiredDescriptionTextId = `DOCUMENT_REQUIRED_DESCRIPTION_${documentRequired.name.toUpperCase()}`;

  const DocumentHeader = (
    <Box>
      <Box style={{ height: "10vh" }} />
      <IconTitle
        icon={
          <ThemedAsset
            id={assetIds.ICON_CLOUD_UPLOAD}
            width={ICON_TITLE_HEIGHT}
            height={ICON_TITLE_HEIGHT}
            style={{ color: palette.secondary.main }}
          />
        }
        title={
          FILE_TYPE[documentRequired.name]
            ? FILE_TYPE[documentRequired.name]
            : documentRequired.description
        }
      />
      <Typography
        variant="body2"
        color="light"
        align="center"
        style={{ display: "block" }}
      >
        {dictionaryValues[documentRequiredDescriptionTextId]
          ? dictionaryValues[documentRequiredDescriptionTextId]
          : documentRequired.description}
      </Typography>
      <Box pb={4} />
    </Box>
  );

  return (
    <>
      <ResponsiveDialog
        PaperProps={{
          "aria-label": "Document required confirmation dialog",
        }}
        displayCondition={true}
        fullScreenCondition={true}
        open={confirmationState}
        className={fadeOut === true && "disappear-anim"}
      >
        <Container style={{ padding: "1rem 1rem", height: "100%" }}>
          <ResponsiveLayout>
            <Box style={{ height: "10vh" }} />
            <Box>
              <Typography
                variant="body1"
                align="center"
                style={{ display: "block" }}
              >
                <Text strong tId="SAVE_CHANGES_PROMT" />
              </Typography>
            </Box>
            <Box>
              <Button
                fullWidth
                color="secondary"
                variant="text"
                component="span"
                onClick={onClose}
              >
                <Text tId="DISCARD_CHANGES" />
              </Button>
              <Box pb={3} />
              <MainButton
                fullWidth
                color="primary"
                variant="contained"
                component="span"
                onClick={onDone}
              >
                <Text tId="SAVE_CHANGES" />
              </MainButton>
            </Box>
          </ResponsiveLayout>
        </Container>
      </ResponsiveDialog>
      <ResponsiveDialog
        PaperProps={{
          "aria-label": "Document required dialog",
        }}
        displayCondition={true}
        fullScreenCondition={true}
        onClose={onClickClose}
        open={open}
        loading={filesUploading}
        className={fadeOut === true && "disappear-anim"}
      >
        <Container style={{ padding: "1rem 1rem", height: "100%" }}>
          <ResponsiveLayout>
            <Box>
              {DocumentHeader}
              <input
                value={""}
                type="file"
                accept="text/plain, application/json, application/pdf, image/jpeg, image/png, text/html"
                id={documentRequired.name}
                name={documentRequired.name}
                key={documentRequired.name}
                disabled={filesUploading}
                style={{
                  display: "none",
                }}
                onChange={(e) => addFile(e, documentRequired)}
              />
              <label
                htmlFor={documentRequired.name}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById(documentRequired.name).click();
                  }
                }}
              >
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  component="span"
                >
                  <Text tId="ADD_FILE_ACTION" />
                </Button>
              </label>
              <Box pb={2} />

              {documentRequired?.uploaded_files.map((uploadedFile, index) => (
                <DocumentationFile
                  key={uploadedFile.name}
                  number={index + 1}
                  file={uploadedFile}
                  document={documentRequired}
                  isUploaded={true}
                  isUploading={true}
                />
              ))}
              {files.length > 0 &&
                files?.map((file) => (
                  <DocumentationFile
                    key={file.name}
                    file={file}
                    document={documentRequired}
                    isUploaded={false}
                    isUploading={filesUploading}
                    removeFile={removeFile}
                  />
                ))}
            </Box>
            {documentRequired.status === DocumentStatus.INCIDENCE &&
              documentRequired.annotation !== null && (
                <Box>
                  <InformationMessage
                    variant={{
                      type: "warning",
                      icon: (
                        <i className="material-icons" style={{ fontSize: 20 }}>
                          warning
                        </i>
                      ),
                    }}
                    isScreenXs={false}
                    messageContent={{
                      title: <Text tId={"INCIDENCE"} />,
                      body: documentRequired.annotation,
                    }}
                  />
                </Box>
              )}
            {!uploadingState ? (
              <Box>
                <Box mt={4} mb={2} px={4}>
                  <Typography
                    variant="caption"
                    color="textPrimary"
                    align="center"
                    style={{ display: "block" }}
                  >
                    {documentRequired.status === documentStatus.PENDING && (
                      <Text tId={"UPLOAD_FILE_ACTION_ASSERTION"} />
                    )}
                  </Typography>
                </Box>

                <MainButton
                  color="primary"
                  onClick={onDone}
                  disabled={files.length === 0 || filesUploading}
                >
                  <Text tId={"DONE"} />
                </MainButton>
              </Box>
            ) : (
              <Box textAlign={"center"}>
                <CircularProgress
                  className={"transition-all"}
                  size={34}
                  thickness={3}
                  color="secondary"
                />
              </Box>
            )}
          </ResponsiveLayout>
        </Container>
      </ResponsiveDialog>
    </>
  );
}
